// Deducción de dónde están los autobuses a partir de los minutos de llegada a cada parada.
// Puro (sin DOM ni red) para poder testearlo con capturas reales de la API.
//
// Idea: recorriendo las paradas de un trayecto en orden, los minutos del «próximo bus» crecen
// mientras sea el mismo autobús el que va llegando. Cuando de una parada a la siguiente los
// minutos BAJAN, es que hay un bus entre las dos: ya pasó por la anterior (que ahora espera al
// siguiente) y aún no ha llegado a la otra.
//
//   parada   001   002   003   047   048   049   034   128   309
//   minutos   6     8     9    11    12    13     0     1     2
//                                               ↑ bus entre 049 y 034
//
// El tiempo entre paradas sale de esos mismos minutos (el tramo que tarda el bus de verdad);
// si no hay datos suficientes, de la distancia y una velocidad comercial por defecto.

export interface RouteStop {
  id: number;
  name: string;
  dist: number; // metros acumulados desde el inicio del trayecto
}

export interface LiveRoute {
  key: string; // `${line}|${routeId}`
  line: string;
  sense: string;
  destination: string;
  stops: RouteStop[];
}

export interface StopEta {
  destination: string;
  minutes: number;
  // Segundos hasta la llegada, alineados al instante de inferencia por el motor.
  seconds?: number;
  observedAt?: number;
}

// Punto de la línea temporal del bus: a los `t` segundos desde la consulta estará en `d`.
export interface TimelinePoint {
  t: number;
  d: number;
  stopIdx: number | null; // parada a la que corresponde (null = posición inicial)
  min?: number; // minutos que daba la API para esa parada
}

export interface InferredBus {
  line: string;
  routeKey: string;
  sense: string;
  destination: string; // destino que anuncia la API
  fromStopId: number;
  toStopId: number;
  timeline: TimelinePoint[]; // siempre con t creciente; el primero es t=0
  observedAt?: number;
  evidenceKey?: string;
  inferredAt?: number;
}

export interface InferOptions {
  // Destinos que corresponden al otro sentido de la línea. En las cabeceras la API anuncia la
  // próxima salida del sentido contrario: hay que ignorarla en este trayecto.
  excludeDestinations: ReadonlySet<string>;
  defaultSpeed: number; // m/s
  minSpeed: number;
  maxSpeed: number;
  requirePreviousObservation?: boolean;
}

export const DEFAULT_INFER: Omit<InferOptions, "excludeDestinations"> = {
  defaultSpeed: 4.2,
  minSpeed: 2.5,
  maxSpeed: 9,
};

export const normDest = (s: string): string =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/gi, " ")
    .trim()
    .toUpperCase();

// Segundos que suponemos dentro del minuto que da la API (redondea hacia abajo: "0 min" es
// «menos de un minuto»).
// Margen (s) sobre el tiempo del tramo para dar por bueno un bus tras una parada sin minutos.
const GAP_SLACK_S = 120;
// Tope (min) del margen por diferencia de antigüedad entre paradas vecinas.
const MAX_AGE_GAP_MIN = 1.5;

const etaSeconds = (minutes: number): number => (minutes === 0 ? 15 : minutes * 60 + 30);

export function inferBuses(
  route: LiveRoute,
  etasByStop: ReadonlyMap<number, readonly StopEta[]>,
  opts: InferOptions,
): InferredBus[] {
  const stops = route.stops;
  const n = stops.length;
  // Minutos del primer bus que llega a cada parada en este sentido (null = sin dato).
  const selected: (StopEta | null)[] = stops.map((s) => {
    const list = (etasByStop.get(s.id) ?? []).filter(
      (e) => !opts.excludeDestinations.has(normDest(e.destination)),
    );
    if (list.length === 0) return null;
    return list.reduce((a, b) => (a.minutes <= b.minutes ? a : b));
  });
  const first = selected.map((e) => e?.minutes ?? null);
  const secondsAt = (i: number): number => selected[i]?.seconds ?? etaSeconds(first[i]!);
  const destAt = (i: number): string => {
    const list = (etasByStop.get(stops[i]!.id) ?? []).filter(
      (e) => !opts.excludeDestinations.has(normDest(e.destination)),
    );
    list.sort((a, b) => a.minutes - b.minutes);
    return list[0]?.destination ?? route.destination;
  };

  // ¿Empieza un bus nuevo en la parada i? Una bajada de 1 minuto con valores altos suele ser
  // ruido del redondeo de la API, no un bus. Si la parada anterior no espera ningún bus y esta
  // sí, el bus está entre las dos (líneas con un solo bus, como las de pedanías: por detrás de
  // él no hay «siguiente bus» que dé la bajada), pero solo si le da tiempo a llegar desde allí:
  // a veces la API devuelve vacía una parada suelta y el bus saldría muy lejos de donde va.
  // Paradas leídas en momentos distintos: a cada minuto se le descuenta la antigüedad de su
  // lectura y eso arrastra un error del orden de la diferencia de edad entre las dos (el bus no
  // avanza exactamente al ritmo que anunciaba). Una bajada menor que ese margen no es otro bus:
  // sin él, el mismo autobús salía partido en dos («0,9» leído ahora y «0,5» leído hace 2 min).
  const ageGap = (i: number): number => {
    const a = selected[i]?.observedAt;
    const b = selected[i - 1]?.observedAt;
    if (a === undefined || b === undefined) return 0;
    return Math.min(MAX_AGE_GAP_MIN, Math.abs(a - b) / 60_000);
  };

  const startsBus = (i: number): boolean => {
    const prev = first[i - 1];
    const cur = first[i];
    if (cur === null || cur === undefined) return false;
    if (prev === null || prev === undefined) {
      // Una parada consultada sin buses es evidencia; una consulta que falta o ha fallado no.
      if (opts.requirePreviousObservation && !etasByStop.has(stops[i - 1]!.id)) return false;
      const leg = stops[i]!.dist - stops[i - 1]!.dist;
      return cur * 60 <= leg / opts.minSpeed + GAP_SLACK_S;
    }
    const tol = ageGap(i);
    return cur < prev - 1 - tol || (cur <= 1 && cur < prev - tol);
  };

  // Bus en la cabecera a punto de salir: la primera parada no tiene otra antes con la que
  // comparar, así que con «0-1 min» lo damos por allí, esperando la salida.
  const departs = n > 1 && first[0] !== null && first[0]! <= 1;

  const buses: InferredBus[] = [];
  for (let i = departs ? 0 : 1; i < n; i++) {
    if (i > 0 && !startsBus(i)) continue;

    // Tramo que sigue recorriendo ese mismo bus: hasta el siguiente bus o hasta perder el dato.
    let j = i;
    while (j + 1 < n && first[j + 1] !== null && !startsBus(j + 1)) {
      j++;
    }
    const eI = first[i]!;
    const eJ = first[j]!;
    const dI = stops[i]!.dist;
    const dJ = stops[j]!.dist;
    let speed = opts.defaultSpeed;
    if (j > i && eJ - eI >= 2 && dJ > dI) speed = (dJ - dI) / ((eJ - eI) * 60);
    speed = Math.max(opts.minSpeed, Math.min(opts.maxSpeed, speed));

    const dPrev = stops[Math.max(0, i - 1)]!.dist;
    const d0 = Math.max(dPrev, Math.min(dI, dI - secondsAt(i) * speed));
    const timeline: TimelinePoint[] = [{ t: 0, d: d0, stopIdx: null }];
    for (let k = i; k <= j; k++) {
      const last = timeline[timeline.length - 1]!;
      const d = Math.max(stops[k]!.dist, last.d);
      // Nunca más rápido que maxSpeed: con «0 min» en varias paradas seguidas (redondeo de la
      // API) el bus las recorrería en segundos y luego la consulta siguiente lo devolvería atrás.
      const t = Math.max(secondsAt(k), last.t + 5, last.t + (d - last.d) / opts.maxSpeed);
      timeline.push({ t, d, stopIdx: k, min: first[k]! });
    }

    const supporting = selected.slice(Math.max(0, i - 1), j + 1);
    const times = supporting.flatMap((e) => (e?.observedAt === undefined ? [] : [e.observedAt]));
    const evidence = times.length
      ? {
          observedAt: Math.min(...times),
          evidenceKey: supporting
            .map((e, k) => `${stops[Math.max(0, i - 1) + k]!.id}:${e?.observedAt ?? "missing"}`)
            .join("|"),
        }
      : {};
    buses.push({
      line: route.line,
      routeKey: route.key,
      sense: route.sense,
      destination: destAt(i),
      fromStopId: stops[Math.max(0, i - 1)]!.id,
      toStopId: stops[Math.max(1, i)]!.id,
      timeline,
      ...evidence,
    });
  }
  return buses;
}

// Distancia recorrida a los `t` segundos de la consulta (interpolación lineal; al llegar al
// final del tramo conocido el bus se queda quieto hasta la siguiente consulta).
export function distanceAt(timeline: readonly TimelinePoint[], t: number): number {
  if (timeline.length === 0) return 0;
  if (t <= timeline[0]!.t) return timeline[0]!.d;
  for (let k = 1; k < timeline.length; k++) {
    const a = timeline[k - 1]!;
    const b = timeline[k]!;
    if (t <= b.t) return a.d + ((b.d - a.d) * (t - a.t)) / (b.t - a.t);
  }
  return timeline[timeline.length - 1]!.d;
}

// Próxima parada (índice en el trayecto) y segundos que faltan, para el texto del bus.
export function nextStopAt(
  timeline: readonly TimelinePoint[],
  t: number,
): { stopIdx: number; seconds: number } | null {
  for (const p of timeline) {
    if (p.stopIdx !== null && p.t >= t) return { stopIdx: p.stopIdx, seconds: p.t - t };
  }
  return null;
}
